import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { LOCALES, DEFAULT_LOCALE } from '../config/locales';
import { useLocale } from '../context/LocaleContext';

function originFromWindow() {
  if (typeof window === 'undefined') return '';
  return window.location.origin || '';
}

function cleanPath(pathname) {
  if (!pathname) return '/';
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

export default function SeoHead({
  title,
  description,
  keywords,
  image,
  canonicalPath,
  noindex = false,
}) {
  const { locale, getPathForLocale } = useLocale();
  const location = useLocation();

  const origin = originFromWindow();
  const effectiveCanonicalPath = canonicalPath || cleanPath(location.pathname);
  const canonicalUrl = origin ? `${origin}${effectiveCanonicalPath}` : effectiveCanonicalPath;

  const alternates = useMemo(() => {
    const pathForDefault = getPathForLocale(DEFAULT_LOCALE.pathPrefix);
    return LOCALES.map((l) => {
      const p = l.pathPrefix === DEFAULT_LOCALE.pathPrefix ? pathForDefault : getPathForLocale(l.pathPrefix);
      const href = origin ? `${origin}${p}` : p;
      return { hrefLang: l.code, href };
    });
  }, [getPathForLocale, origin]);

  const robots = noindex ? 'noindex, nofollow' : undefined;

  return (
    <Helmet>
      {title ? <title>{title}</title> : null}
      {description ? <meta name="description" content={description} /> : null}
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      {robots ? <meta name="robots" content={robots} /> : null}

      <link rel="canonical" href={canonicalUrl} />
      {alternates.map((a) => (
        <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={a.href} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={alternates[0]?.href || canonicalUrl} />

      <meta property="og:locale" content={locale?.code || DEFAULT_LOCALE.code} />
      {title ? <meta property="og:title" content={title} /> : null}
      {description ? <meta property="og:description" content={description} /> : null}
      <meta property="og:type" content="website" />
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
      {image ? <meta property="og:image" content={image} /> : null}

      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      {title ? <meta name="twitter:title" content={title} /> : null}
      {description ? <meta name="twitter:description" content={description} /> : null}
      {image ? <meta name="twitter:image" content={image} /> : null}
    </Helmet>
  );
}

